import type { Label } from "@/lib/api"

/**
 * LabelChip — the visual primitive used everywhere a label is rendered.
 *
 * Aesthetic: a tiny editorial tag, mono uppercase tracking-wider, no
 * background by default (just hairline border) so a row of chips reads as
 * an annotation, not a candy bar. When `color` is set on the label, the
 * border + text adopt that hue; the swatch is intentionally subtle (no
 * filled pill) so the underlying name still does the talking.
 *
 * Variants:
 *  - default: read-only chip
 *  - selectable: shows visual on-state when `selected`; calls onClick
 *  - removable: shows × button; calls onRemove
 *  - new: dashed border, plus sign — for the "+ add label" affordance
 */
export function LabelChip({
  label,
  selected,
  onClick,
  onRemove,
  size = "sm",
}: {
  label: Label
  selected?: boolean
  onClick?: () => void
  onRemove?: () => void
  size?: "sm" | "md"
}) {
  const color = label.color
  const isInteractive = !!onClick
  // Inline style for color overrides — Tailwind can't dynamically synthesise
  // arbitrary hex shades for every user-defined color.
  const style = color
    ? {
        borderColor: selected ? color : `${color}66`,
        color: selected ? "var(--background)" : color,
        backgroundColor: selected ? color : "transparent",
      }
    : undefined

  const pad = size === "md" ? "px-2.5 py-1" : "px-2 py-0.5"
  const text = size === "md" ? "text-[11px]" : "text-[10px]"
  const base = `inline-flex items-center gap-1.5 rounded border font-mono ${text} uppercase tracking-wider transition-colors`
  const stateClass =
    color
      ? "" // styled via inline `style`
      : selected
        ? "bg-foreground text-background border-foreground"
        : "border-border text-muted-foreground hover:text-foreground"

  const content = (
    <>
      <span>{label.name}</span>
      {onRemove && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation()
            onRemove()
          }}
          aria-label={`Remove label ${label.name}`}
          className="opacity-60 hover:opacity-100 transition-opacity -mr-0.5 ml-0.5"
        >
          ✕
        </button>
      )}
    </>
  )

  if (isInteractive) {
    return (
      <button
        type="button"
        onClick={onClick}
        aria-pressed={selected}
        className={`${base} ${pad} ${stateClass}`}
        style={style}
      >
        {content}
      </button>
    )
  }
  return (
    <span className={`${base} ${pad} ${stateClass}`} style={style}>
      {content}
    </span>
  )
}

/** "+ add label" affordance — dashed, opens the editor. */
export function NewLabelChip({
  onClick,
  label = "+ add label",
}: {
  onClick: () => void
  label?: string
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="inline-flex items-center gap-1 px-2 py-0.5 rounded border border-dashed border-border text-muted-foreground hover:border-foreground hover:text-foreground transition-colors font-mono text-[10px] uppercase tracking-wider"
    >
      {label}
    </button>
  )
}
